import React, { useEffect, useRef, useState } from "react";

import { Box, Typography } from "@mui/material";
import { format, isSameDay, isToday } from "date-fns";
import { makeStyles } from "tss-react/mui";

import AppointmentDayColumn from "./AppointmentDayColumn";
import {
  CalendarAppointment,
  DEFAULT_SCROLL_HOUR,
  DraftSlot,
  HOUR_HEIGHT,
  HOURS,
  formatHourLabel,
  formatMinutes,
  minutesNow,
  minutesToOffset,
} from "./geometry";

type Props = {
  dates: Date[];
  appointments: CalendarAppointment[];
  onView: (id: string) => void;
  onCreate: (date: Date, startMin: number, endMin: number) => void;
  onReschedule: (
    id: string,
    date: Date,
    startMin: number,
    endMin: number
  ) => void;
  draftSlot?: DraftSlot | null;
  onDraftMove?: (date: Date, startMin: number, endMin: number) => void;
};

const GUTTER_WIDTH = 56;
const COLUMN_HEIGHT = 24 * HOUR_HEIGHT;
const SCROLL_VIEWPORT_HEIGHT = 620;

const AppointmentsTimeGrid: React.FC<Props> = ({
  dates,
  appointments,
  onView,
  onCreate,
  onReschedule,
  draftSlot,
  onDraftMove,
}) => {
  const { classes, cx } = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);

  const showsToday = dates.some((date) => isToday(date));
  const [now, setNow] = useState(() => new Date());
  const [nowMinutes, setNowMinutes] = useState(minutesNow);
  // ISO of the day column the placeholder is being dragged over.
  const [dropDate, setDropDate] = useState<string | null>(null);

  // Keep the "now" line and status colouring current, once a minute.
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
      setNowMinutes(minutesNow());
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    const target = showsToday
      ? minutesToOffset(nowMinutes) - SCROLL_VIEWPORT_HEIGHT / 2
      : DEFAULT_SCROLL_HOUR * HOUR_HEIGHT;
    scrollRef.current.scrollTop = Math.max(0, target);
    // Only when the visible range changes, not every minute.
  }, [dates[0]?.toISOString(), dates.length, showsToday]);

  const single = dates.length === 1;

  return (
    <Box className={classes.root}>
      <div className={classes.headerRow}>
        <div className={classes.gutterSpacer} />
        {dates.map((date) => {
          const today = isToday(date);
          const count = appointments.filter((appointment) =>
            isSameDay(appointment.start, date)
          ).length;
          return (
            <div
              key={date.toISOString()}
              className={cx(classes.dayHeader, today && classes.dayHeaderToday)}
            >
              <Typography className={classes.dayName}>
                {format(date, single ? "EEEE" : "EEE").toUpperCase()}
              </Typography>
              <Typography className={classes.dayNumber}>
                {format(date, single ? "MMM d" : "d")}
              </Typography>
              <Typography className={classes.dayCount}>
                {count === 0
                  ? "No appointments"
                  : `${count} appointment${count > 1 ? "s" : ""}`}
              </Typography>
            </div>
          );
        })}
      </div>

      <div
        ref={scrollRef}
        className={classes.scrollBody}
        style={{ maxHeight: SCROLL_VIEWPORT_HEIGHT }}
      >
        <div className={classes.grid} style={{ height: COLUMN_HEIGHT }}>
          <div className={classes.gutter}>
            {HOURS.map((hour) => (
              <span
                key={hour}
                className={classes.hourLabel}
                style={{ top: hour * HOUR_HEIGHT }}
              >
                {hour === 0 ? "" : formatHourLabel(hour)}
              </span>
            ))}
          </div>
          {dates.map((date) => (
            <AppointmentDayColumn
              key={date.toISOString()}
              date={date}
              now={now}
              isToday={isToday(date)}
              appointments={appointments.filter((appointment) =>
                isSameDay(appointment.start, date)
              )}
              onView={onView}
              onCreate={onCreate}
              onReschedule={onReschedule}
              placeholder={
                draftSlot && isSameDay(draftSlot.date, date) ? draftSlot : null
              }
              onPlaceholderMove={onDraftMove}
              isDropTarget={dropDate === date.toISOString()}
              onHoverDate={setDropDate}
            />
          ))}

          {showsToday && (
            <>
              <span
                className={classes.nowLabel}
                style={{ top: minutesToOffset(nowMinutes) }}
              >
                {formatMinutes(nowMinutes)}
              </span>
              <div
                className={classes.nowLine}
                style={{ top: minutesToOffset(nowMinutes) }}
              />
            </>
          )}
        </div>
      </div>

      <Box className={classes.legend}>
        <LegendDot color="#2D87BB" label="Upcoming" />
        <LegendDot color="#FFD98A" label="Current" />
        <LegendDot color="#B8BEC9" label="Finished" />
        <LegendDot color="#FF6262" label="Cancelled" />
        <Typography className={classes.legendHint}>
          Click empty space to add · drag a block to reschedule · click to view
        </Typography>
      </Box>
    </Box>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => {
  const { classes } = useStyles();
  return (
    <span className={classes.legendItem}>
      <span
        className={classes.legendSwatch}
        style={{ backgroundColor: color }}
      />
      <Typography className={classes.legendText}>{label}</Typography>
    </span>
  );
};

const useStyles = makeStyles()(() => ({
  root: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #DEE6F0",
    borderRadius: 8,
    overflow: "hidden",
  },
  headerRow: {
    display: "flex",
    borderBottom: "1px solid #DEE6F0",
  },
  gutterSpacer: {
    width: GUTTER_WIDTH,
    flexShrink: 0,
  },
  dayHeader: {
    position: "relative",
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 4px",
    borderLeft: "1px solid #DEE6F0",
  },
  dayHeaderToday: {
    backgroundColor: "rgba(61,109,240,0.06)",
  },
  dayName: {
    fontSize: 11,
    fontWeight: 700,
    color: "#7D8797",
    letterSpacing: 0.5,
  },
  dayNumber: {
    fontSize: 18,
    fontWeight: 700,
    color: "#363E45",
    lineHeight: 1.2,
  },
  dayCount: {
    fontSize: 10,
    fontWeight: 600,
    color: "#9AA3B2",
    marginTop: 2,
  },
  scrollBody: {
    overflowY: "auto",
    overflowX: "hidden",
  },
  grid: {
    display: "flex",
    position: "relative",
  },
  gutter: {
    width: GUTTER_WIDTH,
    flexShrink: 0,
    position: "relative",
  },
  hourLabel: {
    position: "absolute",
    right: 6,
    fontSize: 10,
    fontWeight: 600,
    color: "#9AA3B2",
    transform: "translateY(-6px)",
  },
  nowLine: {
    position: "absolute",
    left: GUTTER_WIDTH,
    right: 0,
    height: 2,
    backgroundColor: "#E5484D",
    pointerEvents: "none",
    zIndex: 2,
    "&::before": {
      content: '""',
      position: "absolute",
      left: -4,
      top: -3,
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#E5484D",
    },
  },
  nowLabel: {
    position: "absolute",
    left: 0,
    width: GUTTER_WIDTH - 4,
    textAlign: "right",
    transform: "translateY(-6px)",
    fontSize: 10,
    fontWeight: 700,
    color: "#E5484D",
    pointerEvents: "none",
    zIndex: 2,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    borderTop: "1px solid #DEE6F0",
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  legendSwatch: {
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: 3,
  },
  legendText: {
    fontSize: 12,
    color: "#4C515A",
    fontWeight: 600,
  },
  legendHint: {
    fontSize: 12,
    color: "#9AA3B2",
    marginLeft: "auto",
  },
}));

export default AppointmentsTimeGrid;
